import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useEvent } from '../../events/queries';
import {
  useCancelEvent,
  useConfirmEvent,
  useModerateEvent,
  useRemoveRsvp,
  useSetRsvp,
  useUpvoteEvent,
} from '../../events/mutations';
import { confirmationLabel, eventTypeLabel, formatEventWhen } from '../../events/format';
import type { EventDetail, ModerateEventBody } from '../../events/types';
import { EventPhotos } from './EventPhotos';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  ErrorState,
  Heading,
  Spinner,
  Stack,
  Text,
} from '../../ui';

/**
 * One event listing, with the freshness trust signal surfaced prominently and every action the
 * caller is allowed to take: confirm, RSVP, upvote, and — for the owner or an administrator — edit,
 * cancel, manage photos and moderate.
 *
 * @returns The event detail screen.
 */
export function EventDetailPage(): React.JSX.Element {
  const { eventId = '' } = useParams<{ eventId: string }>();
  const { data, isPending, isError, error, refetch } = useEvent(eventId);
  const currentUser = useCurrentUser();

  const confirm = useConfirmEvent(eventId);
  const upvote = useUpvoteEvent(eventId);
  const setRsvp = useSetRsvp(eventId);
  const removeRsvp = useRemoveRsvp(eventId);
  const cancel = useCancelEvent(eventId);
  const moderate = useModerateEvent(eventId);

  const [confirmCancel, setConfirmCancel] = useState(false);

  if (isPending) {
    return (
      <Stack gap="md" align="center">
        <Spinner label="Loading the event" />
        <Text muted>Loading…</Text>
      </Stack>
    );
  }

  if (isError) {
    return <ErrorState message={error.message} onRetry={() => void refetch()} />;
  }

  const isAdmin = currentUser.data?.role === 'Admin';
  const isPendingModeration = data.status === 'PendingModeration';

  return (
    <Stack gap="2xl">
      <Stack gap="md">
        <Stack direction="row" gap="md" align="center" justify="space-between" wrap>
          <Stack gap="xs">
            <Heading level={1}>{data.title}</Heading>
            <Text muted>
              {formatEventWhen(data)} · {eventTypeLabel(data.type)}
            </Text>
            <Text muted>{data.address}</Text>
          </Stack>

          {data.canEdit && (
            <Stack direction="row" gap="sm" wrap>
              <Link className="gm-button gm-button--secondary" to={`/events/${eventId}/edit`}>
                Edit
              </Link>
              {data.status !== 'Cancelled' && (
                <Button variant="danger" onClick={() => setConfirmCancel(true)}>
                  Cancel event
                </Button>
              )}
            </Stack>
          )}
        </Stack>

        <Stack direction="row" gap="sm" wrap align="center">
          {data.status === 'Cancelled' && <Badge tone="danger">Cancelled</Badge>}
          {isPendingModeration && <Badge tone="warning">Pending moderation</Badge>}
          {data.status === 'Rejected' && <Badge tone="danger">Rejected</Badge>}
          {data.source === 'OrganizerVerified' && <Badge tone="success">Verified organiser</Badge>}
          {data.organizerName != null && <Badge tone="neutral">{data.organizerName}</Badge>}
          {data.isStale && <Badge tone="warning">Stale listing</Badge>}
        </Stack>
      </Stack>

      <Card ariaLabel="Freshness">
        <Stack gap="md">
          <Stack direction="row" gap="md" align="center" justify="space-between" wrap>
            <Stack gap="xs">
              <Heading level={2} size="lg">
                Is this still accurate?
              </Heading>
              <Text muted>{confirmationLabel(data)}</Text>
            </Stack>
            {data.canConfirm && (
              <Button
                variant="primary"
                disabled={confirm.isPending}
                onClick={() => confirm.mutate()}
              >
                {confirm.isPending ? 'Confirming…' : 'Confirm it is accurate'}
              </Button>
            )}
          </Stack>
          <Text size="sm" muted>
            Anyone can confirm a listing once a day. The more different people confirm, the more you
            can trust it.
          </Text>
        </Stack>
      </Card>

      {data.status === 'Published' && (
        <Card ariaLabel="Going">
          <Stack gap="md">
            <Heading level={2} size="lg">
              Are you going?
            </Heading>
            <Stack direction="row" gap="sm" wrap>
              <Button
                variant={data.callerRsvp === 'Going' ? 'primary' : 'secondary'}
                disabled={setRsvp.isPending}
                onClick={() => setRsvp.mutate('Going')}
              >
                Going
              </Button>
              <Button
                variant={data.callerRsvp === 'Interested' ? 'primary' : 'secondary'}
                disabled={setRsvp.isPending}
                onClick={() => setRsvp.mutate('Interested')}
              >
                Interested
              </Button>
              {data.callerRsvp != null && (
                <Button disabled={removeRsvp.isPending} onClick={() => removeRsvp.mutate()}>
                  Clear
                </Button>
              )}
            </Stack>
            <Stack direction="row" gap="sm" wrap>
              <Badge tone="neutral">{data.goingCount} going</Badge>
              <Badge tone="neutral">{data.interestedCount} interested</Badge>
              <Button disabled={upvote.isPending} onClick={() => upvote.mutate()}>
                ▲ Upvote ({data.upvotes})
              </Button>
            </Stack>
          </Stack>
        </Card>
      )}

      {data.description != null && data.description !== '' && (
        <Card ariaLabel="About">
          <Stack gap="sm">
            <Heading level={2} size="lg">
              About
            </Heading>
            <Text>{data.description}</Text>
          </Stack>
        </Card>
      )}

      <Card ariaLabel="Photos">
        <EventPhotos eventId={eventId} photos={data.photos} canEdit={data.canEdit} />
      </Card>

      {isAdmin && isPendingModeration && <ModerationActions event={data} moderate={moderate} />}

      <ConfirmDialog
        open={confirmCancel}
        title={`Cancel ${data.title}?`}
        message="Attendees will see the event was called off. This cannot be undone."
        confirmLabel="Cancel event"
        destructive
        onCancel={() => setConfirmCancel(false)}
        onConfirm={() => {
          setConfirmCancel(false);
          cancel.mutate(data.version ?? null);
        }}
      />
    </Stack>
  );
}

/** Inline approve/reject controls shown to an administrator on a pending listing. */
function ModerationActions({
  event,
  moderate,
}: {
  readonly event: EventDetail;
  readonly moderate: ReturnType<typeof useModerateEvent>;
}): React.JSX.Element {
  function decide(decision: ModerateEventBody['decision']): void {
    moderate.mutate({
      decision,
      title: event.title,
      description: event.description,
      type: event.type,
      address: event.address,
      latitude: event.latitude,
      longitude: event.longitude,
      startsAt: event.startsAt,
      endsAt: event.endsAt,
      version: event.version,
    });
  }

  return (
    <Card ariaLabel="Moderation">
      <Stack gap="md">
        <Heading level={2} size="lg">
          Moderation
        </Heading>
        <Text muted>
          Approve to publish this listing, or reject it. Edit first if the details need correcting.
        </Text>
        <Stack direction="row" gap="sm" wrap>
          <Button variant="primary" disabled={moderate.isPending} onClick={() => decide('Approve')}>
            Approve
          </Button>
          <Button variant="danger" disabled={moderate.isPending} onClick={() => decide('Reject')}>
            Reject
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
}
